export type Selection = { start: number; end: number };

export type ClipboardAdapter = {
	setStringAsync: (text: string) => Promise<boolean>;
	getStringAsync: () => Promise<string>;
};

export const getCopyText = ({
	documentSelection,
	value,
	selection,
}: {
	documentSelection?: string;
	value?: string;
	selection?: Selection;
}) => {
	if (documentSelection?.trim()) {
		return documentSelection;
	}
	if (!value) {
		return "";
	}
	if (selection && selection.start !== selection.end) {
		return value.substring(selection.start, selection.end);
	}
	return value;
};

export const getPasteResult = ({
	value,
	pasted,
	selection,
	maxLength,
}: {
	value?: string;
	pasted: string;
	selection?: Selection;
	maxLength?: number;
}) => {
	const current = value ?? "";
	const start = selection?.start ?? current.length;
	const end = selection?.end ?? start;
	const next = current.substring(0, start) + pasted + current.substring(end);
	const constrained = maxLength != null ? next.slice(0, maxLength) : next;
	const cursor = Math.min(start + pasted.length, constrained.length);

	return {
		value: constrained,
		selection: { start: cursor, end: cursor },
	};
};

const selectionsEqual = (a?: Selection, b?: Selection) =>
	a?.start === b?.start && a?.end === b?.end;

export const createClipboardActions = ({
	clipboard,
	getValue,
	getSelection,
	getDocumentSelection,
	onChangeText,
	onSelectionChange,
	maxLength,
}: {
	clipboard: ClipboardAdapter;
	getValue: () => string | undefined;
	getSelection: () => Selection | undefined;
	getDocumentSelection?: () => string | undefined;
	onChangeText: (value: string) => void;
	onSelectionChange?: (selection: Selection) => void;
	maxLength?: number;
}) => {
	const handleCopy = async () => {
		const text = getCopyText({
			documentSelection: getDocumentSelection?.(),
			value: getValue(),
			selection: getSelection(),
		});
		if (!text) {
			return;
		}
		try {
			await clipboard.setStringAsync(text);
		} catch {
			// Clipboard access can be denied by the browser or platform.
		}
	};

	const handleCut = async () => {
		const value = getValue() ?? "";
		const selection = getSelection();
		if (!selection || selection.start === selection.end) {
			return;
		}
		const selectedText = value.substring(selection.start, selection.end);
		try {
			const didCopy = await clipboard.setStringAsync(selectedText);
			if (!didCopy) {
				return;
			}
		} catch {
			return;
		}
		if (
			(getValue() ?? "") !== value ||
			!selectionsEqual(getSelection(), selection)
		) {
			return;
		}
		onChangeText(
			value.substring(0, selection.start) + value.substring(selection.end),
		);
		onSelectionChange?.({ start: selection.start, end: selection.start });
	};

	const handlePaste = async () => {
		const value = getValue() ?? "";
		const selection = getSelection();
		let pasted: string;
		try {
			pasted = await clipboard.getStringAsync();
		} catch {
			return;
		}
		if (
			!pasted ||
			(getValue() ?? "") !== value ||
			!selectionsEqual(getSelection(), selection)
		) {
			return;
		}
		const result = getPasteResult({ value, pasted, selection, maxLength });
		onChangeText(result.value);
		onSelectionChange?.(result.selection);
	};

	return { handleCopy, handleCut, handlePaste };
};
